import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isEnLocale = () => {
  const url = new URL(window.location.href)
  return url.searchParams.get('lang') === LOCALES.EN
}

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

export const getXStateConfigWoofz22V4Wt = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageId.NEWSLETTER_URGE_22V4_WT
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageId.NEWSLETTER_URGE_22V4_WT
        },
        target: PageId.EMAIL_22V4_WT,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_22V4_WT,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_22V4_WT,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.LAST_CHANCE_OFFER_22V4_WT && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.WELCOME_TO_WOOFZ_22V4_WT,
              },
              {
                target: PageId.SUBSCRIPTIONS_22V4_WT,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_22V4_WT && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_22V4_WT,
              },
            ],
            PREVIOUS_FLOW_STEP: {
              target: PageId.CANCEL_OFFER_22V4_WT,
            },
          }),
          ...(page.pageId === PageId.CANCEL_OFFER_22V4_WT && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_22V4_WT,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_22V4_WT && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_22V4_WT,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_22V4_WT && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_22V4_WT,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_22V4_WT && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_22V4_WT,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_22V4_WT && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_NOVE8,
          }),
          ...(page.pageId === PageId.ACCOUNT_NOVE8 && {
            NEXT_FLOW_STEP: [
              {
                target: PageId.DOWNLOAD_22V4_WT,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.DOWNLOAD_22V4_WT]: {
    type: 'final',
  },
})
